import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Checkbox, Dropdown, DropdownMenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

// Mirrors the engine's Joi cap on POST /case/{rn}/review reasoning.
const REASONING_MAX_CHARS = 5000;

// A tool, never a queue: reviews are optional, and a HUMAN record overrules
// the teacher's grade for this case (the shadow-week thumbs-down mechanism).
const CaseReviewCard = (props) => {
  const {
    caseGuide,
    caseGuideLoaded,
    submitReview,
  } = props;

  const [grade, setGrade] = React.useState(null);
  const [failureCategory, setFailureCategory] = React.useState('');
  const [reasoning, setReasoning] = React.useState('');
  const [excludeGoldenSet, setExcludeGoldenSet] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitFailed, setSubmitFailed] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const failureCategories = caseGuide?.rubric?.failureCategories || [];
  const canSubmit = Boolean(caseGuide) && Boolean(grade) && !isSubmitting;

  const handleGradeChange = (event, nextGrade) => {
    if (!nextGrade) {
      return;
    }

    setGrade(nextGrade);
    setSubmitted(false);

    if (nextGrade === 'PASS') {
      setFailureCategory('');
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setSubmitFailed(false);
    setSubmitted(false);

    try {
      await submitReview({
        grade,
        failureCategory: grade === 'FAIL' && failureCategory ? failureCategory : undefined,
        reasoning: reasoning.trim() || undefined,
        goldenSet: excludeGoldenSet ? false : undefined,
      });

      setGrade(null);
      setFailureCategory('');
      setReasoning('');
      setExcludeGoldenSet(false);
      setSubmitted(true);
    } catch {
      setSubmitFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #ECECF1',
        borderRadius: '10px',
        px: 3,
        py: 2.5,
      }}
    >
      <Stack gap={1.5}>
        <Stack gap={0.5}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#050A14',
            }}
          >
            <Translate expression='case.Detail.Review.Title' />
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#9A9BAE',
            }}
          >
            <Translate expression='case.Detail.Review.Subtitle' />
          </Typography>
        </Stack>
        {
          caseGuideLoaded && !caseGuide &&
          <Typography
            sx={{
              fontSize: '12px',
              color: '#B77900',
            }}
          >
            <Translate expression='case.Detail.Review.NoGuide' />
          </Typography>
        }
        <ToggleButtonGroup
          exclusive
          onChange={handleGradeChange}
          size='small'
          value={grade}
        >
          <ToggleButton
            value='PASS'
            sx={{
              'px': '16px',
              'textTransform': 'none',
              'fontWeight': 700,
              '&.Mui-selected': {
                color: '#008A00',
                backgroundColor: '#E3FAE5',
              },
            }}
          >
            <Translate expression='case.Detail.Review.Pass' />
          </ToggleButton>
          <ToggleButton
            value='FAIL'
            sx={{
              'px': '16px',
              'textTransform': 'none',
              'fontWeight': 700,
              '&.Mui-selected': {
                color: '#C4512C',
                backgroundColor: '#FFF1EC',
              },
            }}
          >
            <Translate expression='case.Detail.Review.Fail' />
          </ToggleButton>
        </ToggleButtonGroup>
        {
          grade === 'FAIL' && caseGuide && failureCategories.length === 0 &&
          <Typography
            sx={{
              fontSize: '12px',
              color: '#B77900',
            }}
          >
            <Translate expression='case.Detail.Review.NoRubric' />
          </Typography>
        }
        {
          grade === 'FAIL' && failureCategories.length > 0 &&
          <Dropdown
            label={translate('case.Detail.Review.CategoryLabel')}
            onChange={setFailureCategory}
            value={failureCategory}
          >
            {
              failureCategories.map((category) => (
                <DropdownMenuItem
                  key={category.key}
                  value={category.key}
                >
                  {category.label}
                </DropdownMenuItem>
              ))
            }
          </Dropdown>
        }
        <TextField
          fullWidth
          multiline
          minRows={2}
          inputProps={{maxLength: REASONING_MAX_CHARS}}
          placeholder={translate('case.Detail.Review.ReasoningPlaceholder')}
          value={reasoning}
          onChange={setReasoning}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '13px',
            },
          }}
        />
        <Checkbox
          checked={excludeGoldenSet}
          onChange={setExcludeGoldenSet}
          label={
            <Stack>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#526071',
                }}
              >
                <Translate expression='case.Detail.Review.ExcludeGoldenSet' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#9A9BAE',
                }}
              >
                <Translate expression='case.Detail.Review.ExcludeGoldenSetHint' />
              </Typography>
            </Stack>
          }
        />
        <Stack direction='row' sx={{gap: 2, alignItems: 'center'}}>
          <Button
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{
              'alignSelf': 'flex-start',
              'px': '16px',
              'py': '8px',
              'borderRadius': '8px',
              'backgroundColor': '#DA7955',
              'boxShadow': 'none',
              'color': '#FFFFFF',
              'fontSize': '13px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                backgroundColor: '#C4512C',
                boxShadow: 'none',
              },
            }}
            variant='contained'
          >
            {
              isSubmitting ?
                translate('case.Detail.Review.Submitting') :
                translate('case.Detail.Review.Submit')
            }
          </Button>
          {
            submitFailed &&
            <Typography sx={{fontSize: '12px', color: '#D85D31'}}>
              <Translate expression='case.Detail.Review.SubmitFailed' />
            </Typography>
          }
          {
            submitted &&
            <Typography sx={{fontSize: '12px', color: '#008A00'}}>
              <Translate expression='case.Detail.Review.Submitted' />
            </Typography>
          }
        </Stack>
      </Stack>
    </Box>
  );
};

CaseReviewCard.propTypes = {
  caseGuide: PropTypes.object,
  caseGuideLoaded: PropTypes.bool.isRequired,
  submitReview: PropTypes.func.isRequired,
};

export default React.memo(CaseReviewCard);
